import type {
  DeviceInfo,
  NativePlayer,
} from "@screencloud/native-players-helpers";
import type { DeviceDetails } from "@screencloud/rdm-api-client";

import type { ScreenDeviceStateData } from "../../types.g";

/** Device API `state` JSON (extends shadow types with production fields). */
export type DeviceApiState = Record<string, unknown>;

/** Keys of `T` whose value is a boolean flag (excludes numeric capabilities). */
type BooleanKeys<T> = {
  [K in keyof T]-?: NonNullable<T[K]> extends boolean ? K : never;
}[keyof T];

const API_SCHEMA_CAPABILITY_MAP: Record<
  string,
  {
    cap?: BooleanKeys<NonNullable<NativePlayer["capabilities"]>>;
    cmd?: BooleanKeys<NonNullable<NativePlayer["screen_api_command"]>>;
  }
> = {
  "device.reboot": { cap: "reboot", cmd: "reboot" },
  "device.clear_cache": { cap: "clear_cache", cmd: "clear_cache" },
  "app.restart": { cap: "restart", cmd: "restart" },
  "device.restart_player_app": { cap: "restart", cmd: "restart" },
  "device.set_mute": { cap: "mute", cmd: "mute" },
  "app.refresh": { cap: "refresh", cmd: "refresh" },
  "screen.capture": { cap: "screenshot" },
  "os.channel": { cap: "change_update_channel", cmd: "change_update_channel" },
};

export function parseUpdatedAtMs(iso: unknown): number | undefined {
  if (typeof iso !== "string" || !iso) return undefined;
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? undefined : ms;
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return undefined;
  }
  return value as Record<string, unknown>;
}

export function pickString(...values: unknown[]): string | undefined {
  for (const v of values) {
    if (typeof v === "string" && v.length > 0) return v;
  }
  return undefined;
}

/**
 * Picks the MAC address for the active connection: the ethernet MAC when the
 * device is on ethernet, otherwise the wifi MAC. Returns undefined when the
 * matching MAC is missing — does not fall back to the other interface.
 */
export function pickMacAddress(
  metaMac: Record<string, unknown> | undefined,
  connectionType: unknown,
): string | undefined {
  return connectionType === "ethernet"
    ? pickString(metaMac?.ethernet)
    : pickString(metaMac?.wifi);
}

export function formatTemperatures(temperatures: unknown): string | undefined {
  if (temperatures == null) return undefined;
  if (typeof temperatures === "string") return temperatures;
  const record = asRecord(temperatures);
  if (!record) return String(temperatures);

  const parts = Object.entries(record)
    .filter(([, v]) => v != null)
    .map(([key, v]) => `${key}: ${v}`);

  return parts.length > 0 ? parts.join(", ") : undefined;
}

export function formatDailyRestartFromState(dailyRestart: unknown): {
  daily_restart?: string;
  daily_restart_time?: string;
} {
  const dr = asRecord(dailyRestart);
  if (!dr) return {};

  const enabled = dr.enabled === true;
  const hour = pickString(dr.hour);
  const minute = pickString(dr.minute) ?? "00";
  const time =
    hour != null
      ? `${hour.padStart(2, "0")}:${minute.padStart(2, "0")}`
      : undefined;

  return {
    daily_restart: enabled ? "on" : "off",
    ...(time ? { daily_restart_time: time } : {}),
  };
}

export function capabilitiesFromApiSchema(
  apiSchema: unknown,
): Pick<NativePlayer, "capabilities" | "screen_api_command"> {
  const schema = asRecord(apiSchema);
  if (!schema) {
    return { capabilities: {}, screen_api_command: {} };
  }

  const capabilities: NonNullable<NativePlayer["capabilities"]> = {};
  const screen_api_command: NonNullable<NativePlayer["screen_api_command"]> =
    {};

  for (const commandType of Object.keys(schema)) {
    const mapping = API_SCHEMA_CAPABILITY_MAP[commandType];
    if (!mapping) continue;
    if (mapping.cap) capabilities[mapping.cap] = true;
    if (mapping.cmd) screen_api_command[mapping.cmd] = true;
  }

  return { capabilities, screen_api_command };
}

export function getMuteFromDeviceState(
  deviceState: ScreenDeviceStateData | undefined,
): boolean | undefined {
  if (!deviceState?.state) return undefined;
  const settings = asRecord((deviceState.state as DeviceApiState).settings);
  return typeof settings?.mute === "boolean" ? settings.mute : undefined;
}

/**
 * Maps Device API `ScreenDeviceStateData` into legacy `DeviceInfo` for existing UI.
 */
export function deviceStateAdapterToDeviceInfo(
  deviceState: ScreenDeviceStateData,
): Partial<DeviceInfo> | null {
  if (!deviceState.isOnline || !deviceState.state) return null;

  const state = deviceState.state as DeviceApiState;
  const network = asRecord(state.network);
  const health = asRecord(state.health);
  const memory = asRecord(state.memory);
  const storage = asRecord(state.storage);
  const hardware = asRecord(state.hardware);
  const display = asRecord(hardware?.display);
  const settings = asRecord(state.settings);
  const signage = asRecord(state.signage);

  // os, hardware (platform/model/architect/mac), and timezone live under
  // `metadata`, NOT `state`. `state` only carries live telemetry (network,
  // health, memory, storage, display dims, settings, signage, hardware_id).
  const metadata = asRecord(deviceState.metadata);
  const metaOs = asRecord(metadata?.os);
  const metaHardware = asRecord(metadata?.hardware);
  const metaScreen = asRecord(metaHardware?.screen);
  const metaTime = asRecord(metadata?.time);
  const metaMac = asRecord(metaHardware?.mac_addresses);

  const updated_at =
    parseUpdatedAtMs(deviceState.updatedAt) ??
    parseUpdatedAtMs(state.last_seen) ??
    parseUpdatedAtMs(state.last_seen_at);

  const dailyRestartSettings = formatDailyRestartFromState(
    settings?.daily_restart,
  );

  const { capabilities, screen_api_command } = capabilitiesFromApiSchema(
    deviceState.apiSchema,
  );

  // OS version is reported split as version_name + version_code; legacy UI
  // expects the combined "name (code)" string, e.g. "1.3.4-14-g5e0749d (0)".
  const osVersionName = pickString(metaOs?.version_name, metaOs?.version);
  const osVersionCode = pickString(metaOs?.version_code);
  const osVersion = osVersionName
    ? osVersionCode
      ? `${osVersionName} (${osVersionCode})`
      : osVersionName
    : undefined;

  const platform = pickString(
    metaHardware?.platform,
    metaOs?.name,
  )?.toLowerCase();
  const orientation = pickString(
    metaScreen?.native_orientation,
    settings?.orientation,
    display?.orientation,
  );

  const native_player: NativePlayer = {
    // hardware_id is the player's stable id (legacy `native_player.id`); the
    // Device API exposes it on both state and metadata.
    id: pickString(
      state.hardware_id,
      metaHardware?.hardware_id,
      deviceState.deviceId,
    ),
    // The SC player-app version is not reported by the Device API yet; leave
    // undefined rather than mislabel the OS version as the player version.
    version: undefined,
    platform,
    timezone: pickString(metaTime?.timezone),
    settings: {
      mute: typeof settings?.mute === "boolean" ? settings.mute : undefined,
      ...dailyRestartSettings,
      media_cache_port:
        typeof settings?.media_cache_port === "number"
          ? settings.media_cache_port
          : undefined,
      local_proxy_port:
        typeof settings?.local_proxy_port === "number"
          ? settings.local_proxy_port
          : undefined,
      orientation,
    },
    capabilities,
    screen_api_command,
    custom_data: undefined,
  };

  const scaleFactor =
    typeof metaScreen?.scale_factor === "number"
      ? metaScreen.scale_factor
      : undefined;

  const device: NonNullable<DeviceInfo["device"]> = {
    ip: pickString(network?.ip_address),
    hostname: pickString(network?.hostname),
    mac_address: pickMacAddress(metaMac, network?.connection_type),
    model: pickString(metaHardware?.model, metaHardware?.device_model),
    available_memory: pickString(memory?.available, health?.available_memory),
    total_memory: pickString(memory?.total, health?.total_memory),
    available_storage: pickString(
      storage?.available,
      health?.available_storage,
    ),
    total_storage: pickString(storage?.total, health?.total_storage),
    temperatures: formatTemperatures(health?.temperatures),
    display: {
      width: typeof display?.width === "number" ? display.width : undefined,
      height: typeof display?.height === "number" ? display.height : undefined,
      scaleFactor,
      orientation,
    },
    os: {
      version: osVersion,
      type: pickString(metaOs?.type, metaOs?.name),
      model: pickString(metaOs?.model, metaHardware?.model),
      architect: pickString(metaOs?.architect, metaHardware?.architect),
    },
  };

  const web_player: DeviceInfo["web_player"] = signage
    ? {
        content_player: {
          screen_id: pickString(signage.screen_id),
          device_id: pickString(signage.device_id),
          org_id: pickString(signage.org_id),
          space_id: pickString(signage.space_id),
        },
      }
    : undefined;

  const partial: Partial<DeviceInfo> = {
    device,
    native_player,
    web_player,
    ...(updated_at != null ? { updated_at } : {}),
  };

  return partial;
}

/**
 * Adapts `deviceState.state.network` (new Device API shape) to the
 * `DeviceDetails["shadow"]["network"]` shape consumed by the Network component.
 * Reads state directly — independent of `isOnline` so last-known data shows
 * when the device is offline.
 */
export function deviceStateToSCOSNetworkInfo(
  deviceState: ScreenDeviceStateData,
): NonNullable<DeviceDetails["shadow"]["network"]> {
  const state = deviceState.state as DeviceApiState;
  const network = asRecord(state?.network);
  const metadata = asRecord(deviceState.metadata);
  const metaMac = asRecord(asRecord(metadata?.hardware)?.mac_addresses);

  const rssi =
    typeof network?.wifi_rssi === "number" ? network.wifi_rssi : undefined;
  // Convert dBm to a 0–100 quality percentage: 2*(rssi+100), clamped.
  // e.g. −49 dBm → 102 → 100 (Excellent); −80 dBm → 40 (Low)
  const signal =
    rssi !== undefined
      ? String(Math.min(100, Math.max(0, 2 * (rssi + 100))))
      : "";

  const ip = pickString(network?.ip_address);
  const dns = network?.dns;
  // `mode` is a required "wifi" | "ethernet"; default to "wifi" when the
  // device doesn't report a recognised connection type.
  const mode =
    pickString(network?.connection_type) === "ethernet" ? "ethernet" : "wifi";

  return {
    ssid: pickString(network?.wifi_ssid) ?? "",
    mode,
    signal,
    ip_address: ip ? [ip] : [],
    security: pickString(network?.wifi_security) ?? "",
    mac_address: pickMacAddress(metaMac, mode) ?? "",
    dns: Array.isArray(dns) ? (dns as string[]) : [],
    bssid: "",
    speed: "",
    duplex: "",
    routes: { default_route: pickString(network?.gateway) ?? "" },
    client_certificate: "",
    ca_certificate: "",
    domains: [],
    ntp: [],
    proxy: "",
    device: "",
  };
}
